import React, { Fragment, useEffect, useRef, useState } from 'react'
import GoogleMapReact from 'google-map-react'
import { GOOGLE_API_KEY } from '../../env'
import { TargetingCircleInput, TargetingZoneInput } from '../../__generated__/graphql'
import { has, isNil } from 'lodash-es'
import { countriesCenterAndZoom } from '../../utils/countriesCenterAndZoom'
import { theme } from '../../styles/theme/theme'
import { LocationTypesEnum } from '../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'
import {
  BoundaryFeature,
  BoundaryKind,
  TargetingCountryLike,
  findCountryFeature,
  findZoneFeature,
  hasBoundaries,
  useBoundaries,
} from '../../utils/geo/boundaries'

type MapLocation = TargetingCircleInput | TargetingZoneInput | TargetingCountryLike

interface MapProps {
  /** Countries come as ISO codes from the API and as { code, label } objects from the form */
  locations: MapLocation[]
  countryCode: string
  locationType?: LocationTypesEnum
}

// Resolved at call time: GeoTargetingFields imports this module indirectly, so its enum is not ready at load time
const getBoundaryKind = (locationType?: LocationTypesEnum): BoundaryKind | undefined => {
  if (locationType === LocationTypesEnum.Departements) return 'departements'
  if (locationType === LocationTypesEnum.Regions) return 'regions'
  if (locationType === LocationTypesEnum.Countries) return 'countries'
  return undefined
}

const isCountry = (location: MapLocation): location is TargetingCountryLike =>
  typeof location === 'string' || has(location, 'code')

const toPaths = (feature: BoundaryFeature): Array<Array<{ lat: number; lng: number }>> => {
  const polygons = feature.geometry.type === 'Polygon' ? [feature.geometry.coordinates] : feature.geometry.coordinates
  return polygons.flatMap((polygon) => polygon.map((ring) => ring.map(([lng, lat]) => ({ lat, lng }))))
}

// The same red for included and excluded locations
const SHAPE_COLOR = theme.palette.error.main

const MAP_MIN_HEIGHT = '36.5rem'

const GeoTargetingMap: React.FC<MapProps> = ({ locations, countryCode, locationType }) => {
  const [mapReference, setMapReference] = useState(null)
  const [mapsReference, setMapsReference] = useState(null)
  const drawingsRef = useRef<any[]>([])
  const apiKey = GOOGLE_API_KEY
  // Always centred on the adgroup's market, whatever is targeted
  const defaultView = countriesCenterAndZoom[countryCode] ?? countriesCenterAndZoom.fr

  // Départements: French market only. Regions: France and the other European markets. Countries: any market.
  // Without borders for the market, départements and regions keep the stored boxes.
  const boundaryKind = getBoundaryKind(locationType)
  const boundaries = useBoundaries(
    boundaryKind,
    countryCode,
    Boolean(boundaryKind) && hasBoundaries(boundaryKind as BoundaryKind, countryCode)
  )

  const markerOptions = {
    strokeColor: SHAPE_COLOR,
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: SHAPE_COLOR,
    fillOpacity: 0.3,
  }

  const drawCity = (location: TargetingCircleInput): unknown | undefined => {
    if (!isNil(location?.location?.latitude) && !isNil(location?.location?.longitude)) {
      return new (mapsReference as any).Circle({
        center: { lat: location?.location?.latitude, lng: location?.location?.longitude },
        radius: (location?.radius ?? 0) * 1000,
        map: mapReference,
        ...markerOptions,
      })
    }
  }

  const drawDepartementOrRegion = (location: TargetingZoneInput): unknown => {
    const feature = boundaries && findZoneFeature(location, boundaries)
    if (feature) {
      return new (mapsReference as any).Polygon({
        paths: toPaths(feature),
        map: mapReference,
        ...markerOptions,
      })
    }
    return drawBoundingBox(location)
  }

  // A country has no stored shape: without its border there is nothing to draw
  const drawCountry = (feature: BoundaryFeature): unknown =>
    new (mapsReference as any).Polygon({
      paths: toPaths(feature),
      map: mapReference,
      ...markerOptions,
    })

  const drawBoundingBox = (location: TargetingZoneInput): unknown =>
    new (mapsReference as any).Rectangle({
      bounds: {
        north: location?.geometry?.northeast?.latitude,
        south: location?.geometry?.southwest?.latitude,
        east: location?.geometry?.northeast?.longitude,
        west: location?.geometry?.southwest?.longitude,
      },
      map: mapReference,
      ...markerOptions,
    })

  const onApiLoaded = (map: any, maps: any): void => {
    if (map && maps) {
      setMapReference(map)
      setMapsReference(maps)
    }
  }

  const removeDrawings = (): void => {
    drawingsRef.current.forEach((drawing: any) => drawing.setMap(null))
    drawingsRef.current = []
  }

  const displayLocations = (): void => {
    drawingsRef.current = locations
      .map((location) => {
        if (has(location, 'location')) return drawCity(location as TargetingCircleInput)
        if (has(location, 'geometry')) return drawDepartementOrRegion(location as TargetingZoneInput)
        if (isCountry(location) && boundaryKind === 'countries') {
          const feature = boundaries && findCountryFeature(location, boundaries)
          return feature ? drawCountry(feature) : undefined
        }
        return undefined
      })
      .filter(Boolean)
  }

  useEffect(() => {
    if (!mapsReference || !mapReference) return
    displayLocations()
    return removeDrawings
  }, [locations, mapReference, mapsReference, boundaries])

  return (
    <Fragment>
      {/* Fills the height its parent gives it (the geo targeting row), never below MAP_MIN_HEIGHT */}
      <div
        data-testid={'googleMapGeoTargeting'}
        style={{ position: 'relative', flex: '1 1 auto', width: '100%', height: '100%', minHeight: MAP_MIN_HEIGHT }}
      >
        <div style={{ position: 'absolute', inset: 0 }}>
          <GoogleMapReact
            bootstrapURLKeys={{ key: apiKey, libraries: ['places'], id: '__googleMapsScriptId' }}
            defaultCenter={defaultView.center}
            defaultZoom={defaultView.zoom}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({ map, maps }) => onApiLoaded(map, maps)}
            draggable={false}
          />
        </div>
      </div>
    </Fragment>
  )
}

export default GeoTargetingMap
